// ============================================================
// Appearance engine — single source of truth for the theme.
// Colors are stored as HEX (for color pickers) and converted to
// the "H S% L%" triplet that the Tailwind tokens expect via
// hsl(var(--token)). Fonts are curated Google Fonts.
// ============================================================

export const DEFAULT_APPEARANCE = {
  colors: {
    background: '#ffffff',
    foreground: '#141414',
    primary: '#141414',
    primary_foreground: '#fafafa',
    secondary: '#f5f5f5',
    secondary_foreground: '#141414',
    muted: '#f5f5f5',
    muted_foreground: '#737373',
    accent: '#f0f0f0',
    accent_foreground: '#141414',
    border: '#e5e5e5',
    input: '#e5e5e5',
    ring: '#141414',
    button_bg: '#141414',
    button_text: '#fafafa',
  },
  typography: { heading_font: 'EuropeExtendedC', body_font: 'Inter', base_scale: 1.0 },
  hero: {
    slides: [
      { url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80', public_id: null },
      { url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80', public_id: null },
    ],
    interval_ms: 6000,
  },
  logo: {
    mode: 'text',
    text: 'FEDYK Furniture',
    word1: 'FEDYK',
    word1_bold: true,
    word2: 'Furniture',
    word2_bold: false,
    image_scale: 1.0,
    header_image_url: null,
    header_image_public_id: null,
    footer_image_url: null,
    footer_image_public_id: null,
  },
  images: {},
};

// ~12 premium Google Fonts (heading + body share the list)
export const CURATED_FONTS = [
  { name: 'EuropeExtendedC', category: 'Premium Local' },
  { name: 'BankGothic Lt BT', category: 'Premium Local' },
  { name: 'Inter', category: 'Sans' },
  { name: 'Manrope', category: 'Sans' },
  { name: 'Space Grotesk', category: 'Sans' },
  { name: 'DM Sans', category: 'Sans' },
  { name: 'Work Sans', category: 'Sans' },
  { name: 'Jost', category: 'Sans' },
  { name: 'Montserrat', category: 'Sans' },
  { name: 'Poppins', category: 'Sans' },
  { name: 'Playfair Display', category: 'Serif' },
  { name: 'Cormorant Garamond', category: 'Serif' },
  { name: 'Lora', category: 'Serif' },
  { name: 'Libre Baskerville', category: 'Serif' },
];

// Color tokens grouped for the admin UI
export const COLOR_GROUPS = [
  {
    group: 'Backgrounds',
    tokens: [
      { key: 'background', label: 'Page background' },
      { key: 'secondary', label: 'Secondary surface' },
      { key: 'muted', label: 'Muted surface' },
      { key: 'accent', label: 'Accent surface' },
    ],
  },
  {
    group: 'Text',
    tokens: [
      { key: 'foreground', label: 'Primary text' },
      { key: 'muted_foreground', label: 'Muted text' },
      { key: 'secondary_foreground', label: 'On secondary' },
      { key: 'accent_foreground', label: 'On accent' },
    ],
  },
  {
    group: 'Buttons & Primary',
    tokens: [
      { key: 'button_bg', label: 'Button background' },
      { key: 'button_text', label: 'Button text' },
      { key: 'primary', label: 'Primary' },
      { key: 'primary_foreground', label: 'On primary' },
    ],
  },
  {
    group: 'Borders & Inputs',
    tokens: [
      { key: 'border', label: 'Borders' },
      { key: 'input', label: 'Inputs' },
      { key: 'ring', label: 'Focus ring' },
    ],
  },
];

// Hardcoded image slots across the public site
export const IMAGE_SLOTS = [
  { key: 'home_about_preview', group: 'Homepage', label: 'About preview image', default_url: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80' },
  { key: 'home_collection_wardrobes', group: 'Homepage', label: 'Collection fallback — Wardrobes', default_url: 'https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=1200&q=85&auto=format&fit=crop' },
  { key: 'home_collection_doors', group: 'Homepage', label: 'Collection fallback — Doors', default_url: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=85&auto=format&fit=crop' },
  { key: 'home_collection_kitchens', group: 'Homepage', label: 'Collection fallback — Kitchens', default_url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=85&auto=format&fit=crop' },
  { key: 'megamenu_kitchens', group: 'Mega Menu', label: 'Category — Kitchens', default_url: '' },
  { key: 'megamenu_wardrobes', group: 'Mega Menu', label: 'Category — Wardrobes', default_url: '' },
  { key: 'megamenu_doors', group: 'Mega Menu', label: 'Category — Doors', default_url: '' },
  { key: 'megamenu_wall_panels', group: 'Mega Menu', label: 'Category — Wall Panels', default_url: '' },
  { key: 'megamenu_soft_furniture', group: 'Mega Menu', label: 'Category — Soft Furniture', default_url: '' },
  { key: 'megamenu_ready_solutions', group: 'Mega Menu', label: 'Category — Ready Solutions', default_url: '' },
  { key: 'megamenu_custom_furniture', group: 'Mega Menu', label: 'Category — Custom Furniture', default_url: '' },
  { key: 'about_hero', group: 'About', label: 'About — Hero image', default_url: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1920&q=80' },
  { key: 'about_factory', group: 'About', label: 'About — Factory / Craft image', default_url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80' },
  { key: 'auth_login', group: 'Auth', label: 'Login page side image', default_url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80' },
  { key: 'auth_register', group: 'Auth', label: 'Register page side image', default_url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80' },
  { key: 'b2b_hero', group: 'B2B', label: 'B2B hero background', default_url: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1920&q=80' },
  { key: 'b2b_showroom', group: 'B2B', label: 'B2B showroom image', default_url: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80' },
];

// Hero slider speed presets (ms)
export const HERO_SPEED_PRESETS = [
  { key: 'slow', label: 'Slow', ms: 9000 },
  { key: 'standard', label: 'Standard', ms: 6000 },
  { key: 'fast', label: 'Fast', ms: 3000 },
];

// Color presets (partial color overrides merged onto defaults)
export const PRESETS = [
  {
    key: 'brand',
    name: 'Brand Monochrome',
    colors: { ...DEFAULT_APPEARANCE.colors },
  },
  {
    key: 'warm_sand',
    name: 'Warm Sand',
    colors: {
      ...DEFAULT_APPEARANCE.colors,
      background: '#faf7f2', foreground: '#2b2620', secondary: '#f0e9df', muted: '#efe7da',
      muted_foreground: '#8a7d6b', accent: '#e9dfce', accent_foreground: '#2b2620',
      border: '#e2d6c3', input: '#e2d6c3', primary: '#3a342b', primary_foreground: '#faf7f2',
      button_bg: '#3a342b', button_text: '#faf7f2', ring: '#3a342b', secondary_foreground: '#2b2620',
    },
  },
  {
    key: 'dark_luxe',
    name: 'Dark Luxe',
    colors: {
      background: '#0d0d0d', foreground: '#ededed', secondary: '#1a1a1a', secondary_foreground: '#ededed',
      muted: '#1a1a1a', muted_foreground: '#9a9a9a', accent: '#222222', accent_foreground: '#ededed',
      border: '#262626', input: '#262626', primary: '#ededed', primary_foreground: '#0d0d0d',
      button_bg: '#ededed', button_text: '#0d0d0d', ring: '#d4d4d4',
    },
  },
  {
    key: 'editorial_ivory',
    name: 'Editorial Ivory',
    colors: {
      ...DEFAULT_APPEARANCE.colors,
      background: '#fbfaf7', foreground: '#1c1c1c', secondary: '#f2f1ec', muted: '#f2f1ec',
      muted_foreground: '#6f6f68', accent: '#ebe9e2', border: '#e3e1d8', input: '#e3e1d8',
      primary: '#1c1c1c', button_bg: '#1c1c1c', button_text: '#fbfaf7',
    },
  },
];

// ---------------- Helpers ----------------

export function hexToHsl(hex) {
  if (!hex) return '0 0% 0%';
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const lum = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(lum * 100)}%`;
}

const TOKEN_KEYS = [
  'background', 'foreground', 'primary', 'primary_foreground', 'secondary', 'secondary_foreground',
  'muted', 'muted_foreground', 'accent', 'accent_foreground', 'border', 'input', 'ring',
];

const VAR_NAME = (key) => `--${key.replace(/_/g, '-')}`;

// Returns a CSS string overriding :root tokens, fonts, scale + button rule.
export function buildAppearanceCss(config) {
  const cfg = { ...DEFAULT_APPEARANCE, ...(config || {}) };
  const colors = { ...DEFAULT_APPEARANCE.colors, ...(cfg.colors || {}) };
  const typo = { ...DEFAULT_APPEARANCE.typography, ...(cfg.typography || {}) };

  const lines = [];
  TOKEN_KEYS.forEach((k) => { lines.push(`${VAR_NAME(k)}: ${hexToHsl(colors[k])};`); });
  
  // Базовые утилиты Tailwind карточек и всплывающих окон
  lines.push(`--card: ${hexToHsl(colors.background)};`);
  lines.push(`--card-foreground: ${hexToHsl(colors.foreground)};`);
  lines.push(`--popover: ${hexToHsl(colors.background)};`);
  lines.push(`--popover-foreground: ${hexToHsl(colors.foreground)};`);
  lines.push(`--btn-bg: ${hexToHsl(colors.button_bg || colors.primary)};`);
  lines.push(`--btn-text: ${hexToHsl(colors.button_text || colors.primary_foreground)};`);
  lines.push(`--font-heading: '${typo.heading_font}';`);
  lines.push(`--font-body: '${typo.body_font}';`);

  // 🎯 СВЯЗЬ С CMS: Синхронизируем легаси переменные App.css напрямую с цветовой панелью админки
  lines.push(`--cms-bg-canvas: ${colors.background};`);
  lines.push(`--cms-bg-navbar: ${colors.background};`);
  lines.push(`--cms-bg-cards: ${colors.secondary};`);
  lines.push(`--cms-bg-dark-modules: ${colors.foreground};`);
  lines.push(`--cms-bg-footer: ${colors.foreground};`); // Делаем подвал стильным контрастным по умолчанию
  lines.push(`--cms-border-divider: ${colors.border};`);
  
  lines.push(`--cms-text-h1-h2: ${colors.foreground};`);
  lines.push(`--cms-text-h3-h4: ${colors.foreground};`);
  lines.push(`--cms-text-body-main: ${colors.foreground};`);
  lines.push(`--cms-text-body-muted: ${colors.muted_foreground};`);
  lines.push(`--cms-text-links: ${colors.primary};`);
  lines.push(`--cms-text-on-dark: ${colors.background};`); // Автоматическая инверсия текста на темных полях

  const scalePct = Math.round((typo.base_scale || 1) * 100);

  return `:root{${lines.join('')}}
html{font-size:${scalePct}%;}
.btn-primary{background-color:hsl(var(--btn-bg));color:hsl(var(--btn-text));}
.btn-primary:hover{background-color:hsl(var(--btn-bg) / 0.9);}`;
}

// Build a Google Fonts <link> href for the two selected families.
export function googleFontsHref(headingFont, bodyFont) {
  const localFonts = ['EuropeExtendedC', 'BankGothic Lt BT'];
  const fams = Array.from(new Set([headingFont, bodyFont].filter(Boolean)))
    .filter((f) => !localFonts.includes(f));

  if (fams.length === 0) return null;
  const params = fams
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:wght@300;400;500;600;700`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}

// Resolve an image slot URL: override → default.
export function resolveSlot(images, slotKey, fallbackUrl) {
  const o = images && images[slotKey];
  return (o && o.url) || fallbackUrl;
}

// Inject (or update) the appearance <style> + Google Fonts <link> in <head>.
export function applyAppearanceToDom(config, styleId = 'fedyk-appearance-published', fontLinkId = 'fedyk-fonts') {
  if (typeof document === 'undefined') return;
  let style = document.getElementById(styleId);
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    document.head.appendChild(style);
  }
  style.textContent = buildAppearanceCss(config);

  const href = googleFontsHref(config?.typography?.heading_font, config?.typography?.body_font);
  if (href) {
    let link = document.getElementById(fontLinkId);
    if (!link) {
      link = document.createElement('link');
      link.id = fontLinkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  } else {
    document.getElementById(fontLinkId)?.remove();
  }
}

export function removeAppearanceStyle(styleId, fontLinkId) {
  if (typeof document === 'undefined') return;
  document.getElementById(styleId)?.remove();
  if (fontLinkId) document.getElementById(fontLinkId)?.remove();
}
